import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { AgentPanelService } from '../agent-panel.service';
import { AgentContextService } from '../agent-context.service';
import { AgentsConversationStore } from '../agent-conversation.store';
import { AgentsChatService } from '../agents-chat.service';
import { AgentCampaignSource } from '../agent-campaign-source.service';
import { AgentSiteSource } from '../agent-site-source.service';
import { AgentAudienceSource } from '../agent-audience-source.service';
import { AgentMentionOption } from '../agent-mention.model';
import { rankMentions } from '../agent-mentions';
import {
  MentionToken,
  adjacentMentionChip,
  createMentionChip,
  findMentionToken,
  serializeEditor,
} from '../agent-composer-dom';
import { PendingConfirmation, TemplateSelection, TurnRating } from '../agent-conversation.model';
import { CampaignService } from '../../shared/services/campaign.service';
import { CurrentSiteIdStore } from '../../core/current-site-id.store';
import { TargetSite } from '../../core/target-site';
import { Audience } from '../../audience/components/audience.model';
import { FlyoutService } from '../../flyout/flyout.service';
import { FlyoutDataType, FlyoutOpeningData } from '../../flyout/flyout-models';
import { markdownToText } from '../../shared/pipes/markdown.pipe';

@Component({
  selector: 'app-agent-panel',
  templateUrl: './agent-panel.component.html',
  styleUrls: ['./agent-panel.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentPanelComponent {
  private readonly panel = inject(AgentPanelService);
  private readonly contextService = inject(AgentContextService);
  private readonly store = inject(AgentsConversationStore);
  private readonly translate = inject(TranslateService);
  private readonly campaignService = inject(CampaignService);
  private readonly currentSiteIdStore = inject(CurrentSiteIdStore);
  private readonly flyoutService = inject(FlyoutService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly chat = inject(AgentsChatService);
  private readonly campaignSource = inject(AgentCampaignSource);
  private readonly siteSource = inject(AgentSiteSource);
  private readonly audienceSource = inject(AgentAudienceSource);

  protected readonly enabled = this.panel.enabled;
  protected readonly isOpen = this.panel.isOpen;
  protected readonly isMobile = this.panel.isMobile;
  protected readonly messages = this.store.messages;
  protected readonly streaming = this.store.streaming;
  protected readonly suggestions = this.store.suggestions;
  protected readonly hasMessages = computed(() => this.messages().length > 0);

  // Read-only history view. Swaps the live conversation for the past-conversations browser; the
  // composer is hidden while it's open (view-only, no resume).
  protected readonly showHistory = signal(false);

  protected readonly mentionQuery = signal<string | null>(null);
  protected readonly mentionResults = signal<AgentMentionOption[]>([]);
  protected readonly mentionActiveIndex = signal(0);
  protected readonly mentionOpen = computed(() => this.mentionQuery() !== null && this.mentionResults().length > 0);
  // The `@` token the next pick replaces with a chip. Picked mentions live as chip nodes in the
  // editor, not a side list, so there's nothing to reconcile at send time and no half-edited
  // reference can be sent.
  private mentionToken: MentionToken | null = null;
  private focusBeforeOpen: HTMLElement | null = null;

  // Auto-scroll follows new content only while the user is near the bottom; scrolling up pauses it
  // and shows the jump-to-latest button.
  private readonly isPinnedToBottom = signal(true);
  protected readonly showJumpToLatest = computed(() => this.hasMessages() && !this.isPinnedToBottom());
  private readonly latestConfirmationId = computed(() => this.messages().at(-1)?.confirmation?.toolCallId);

  // The polite live region speaks only when this text changes, so "Thinking" is read once per turn.
  protected readonly liveAnnouncement = computed(() => {
    const list = this.messages();
    const last = list[list.length - 1];
    if (!last || last.role !== 'assistant') {
      return '';
    }
    if (last.status === 'streaming') {
      return this.translate.instant('agent.thinking');
    }
    if (last.status === 'error') {
      return this.translate.instant('agent.error');
    }
    if (last.status === 'stopped') {
      return this.translate.instant('agent.stopped');
    }
    if (last.confirmation) {
      return this.translate.instant('agent.awaiting-approval', { summary: last.confirmation.summary });
    }
    return markdownToText(
      (last.blocks ?? [])
        .filter((block) => block.kind === 'text' && !!block.text)
        .map((block) => block.text)
        .join('\n\n'),
    );
  });

  // The surface the assistant is grounded on (route-derived), used for the composer chip, the
  // empty-state intro, and the example prompts. Reacts to navigation via the context signal.
  private readonly surfaceKey = computed<string>(() => {
    const context = this.contextService.context().context;
    return context === 'editor' || context === 'analytics' || context === 'campaign' || context === 'dashboard'
      ? context
      : 'general';
  });

  protected readonly contextLabelKey = computed(() => `agent.context.${this.surfaceKey()}`);
  protected readonly emptyIntroKey = computed(() => `agent.empty.${this.surfaceKey()}`);

  // The campaign the current context is scoped to (editor / analytics / campaign surfaces), if any.
  private readonly contextCampaignId = computed<number | undefined>(() => {
    const context = this.contextService.context();
    return context.context === 'editor' || context.context === 'analytics' || context.context === 'campaign'
      ? context.campaignId
      : undefined;
  });

  // Resolved lazily (see the constructor effect); the UI falls back to the id until then.
  private readonly resolvedCampaign = signal<{ id: number; name: string } | null>(null);

  protected readonly contextEntityName = computed<string>(() => {
    const campaignId = this.contextCampaignId();
    if (campaignId === undefined) {
      return '';
    }
    const resolved = this.resolvedCampaign();
    return resolved?.id === campaignId && resolved.name.length > 0 ? resolved.name : '';
  });

  // Grounded facts for the "what I can see" popover. Effective permissions are server-side and not
  // sent down, so they're deliberately not claimed here.
  protected readonly contextDetails = computed<{ label: string; value: string }[]>(() => {
    const context = this.contextService.context();
    const details: { label: string; value: string }[] = [];
    const campaignId = this.contextCampaignId();
    if (campaignId !== undefined) {
      const name = this.contextEntityName();
      details.push({
        label: 'agent.inspect.campaign',
        value: name.length > 0 ? `${name} (#${campaignId})` : `#${campaignId}`,
      });
    }
    if (context.context === 'editor') {
      details.push({ label: 'agent.inspect.widget', value: `#${context.widgetId}` });
    }
    const siteId = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (siteId) {
      details.push({ label: 'agent.inspect.site', value: `#${siteId}` });
    }
    return details;
  });

  // Fires when the active language changes, so the array-valued example prompts (which can't go
  // through the `translate` pipe) recompute. Emits null until the first change.
  private readonly langChange = toSignal(this.translate.onLangChange, { initialValue: null });

  // Example prompts for the empty state, per surface. Sourced from i18n arrays and tolerant of the
  // index-keyed-object shape the translation loader can produce for arrays.
  protected readonly examplePrompts = computed<string[]>(() => {
    this.langChange();
    const value = this.translate.instant(`agent.examples.${this.surfaceKey()}`);
    if (Array.isArray(value)) {
      return value as string[];
    }
    return value && typeof value === 'object' ? (Object.values(value) as string[]) : [];
  });

  // The composer is a contenteditable editor, not a plain input (chips are real inline nodes), so its
  // content is tracked manually rather than via a form control.
  private readonly composerHasContent = signal(false);
  protected readonly canSend = computed(() => !this.streaming() && this.composerHasContent());
  protected readonly isEmpty = computed(() => !this.composerHasContent());

  // Feature-detected so the mic button is hidden where MediaRecorder / getUserMedia are unavailable
  // (older Safari, insecure origins).
  protected readonly voiceSupported =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
  protected readonly voiceState = signal<VoiceState>('idle');
  protected readonly voiceError = signal<string>('');
  protected readonly voiceIcon = computed<string>(() => {
    switch (this.voiceState()) {
      case 'recording':
        return 'stop';
      case 'starting':
      case 'transcribing':
        return 'hourglass_top';
      default:
        return 'mic';
    }
  });
  protected readonly voiceButtonLabel = computed<string>(() => {
    switch (this.voiceState()) {
      case 'recording':
        return 'agent.voice.stop';
      case 'starting':
        return 'agent.voice.starting';
      case 'transcribing':
        return 'agent.voice.transcribing';
      default:
        return 'agent.voice.start';
    }
  });
  // 'starting' and 'transcribing' are the busy states: button disabled, icon spins.
  protected readonly voiceBusy = computed<boolean>(
    () => this.voiceState() === 'starting' || this.voiceState() === 'transcribing',
  );

  private mediaRecorder: MediaRecorder | undefined;
  private mediaStream: MediaStream | undefined;
  private recordedChunks: Blob[] = [];

  private readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');
  private readonly composerInput = viewChild<ElementRef<HTMLDivElement>>('composerInput');
  private readonly panelEl = viewChild<ElementRef<HTMLElement>>('panelEl');

  private readonly resizing = signal(false);

  constructor() {
    // Follow new content while pinned. Pin read non-reactively (run on content, not pin flips) and
    // re-checked in the frame so a scroll-up mid-frame still wins.
    effect(() => {
      this.messages();
      const element = this.scrollContainer()?.nativeElement;
      if (!element || !untracked(this.isPinnedToBottom)) {
        return;
      }
      requestAnimationFrame(() => {
        if (untracked(this.isPinnedToBottom)) {
          element.scrollTop = element.scrollHeight;
        }
      });
    });

    // A confirmation card waits on the user, so it is brought into view even after a scroll-up.
    effect(() => {
      if (this.latestConfirmationId()) {
        requestAnimationFrame(() => this.jumpToLatest());
      }
    });

    // Focus returns to where it was before opening, so it is never left on the inert panel.
    effect(() => {
      if (this.isOpen()) {
        this.focusBeforeOpen = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        this.focusComposer();
        return;
      }
      const returnTo = this.focusBeforeOpen;
      this.focusBeforeOpen = null;
      const panel = untracked(this.panelEl)?.nativeElement;
      const active = document.activeElement;
      if (returnTo?.isConnected && (active === document.body || !!panel?.contains(active))) {
        returnTo.focus();
      }
    });

    // Seeds a launcher's handed-over text into the composer, unsent. The draft stays queued until the
    // composer exists, so nothing is dropped.
    effect(() => {
      const draft = this.panel.pendingDraft();
      if (!draft || !this.isOpen()) {
        return;
      }
      if (untracked(this.showHistory)) {
        this.showHistory.set(false);
      }
      if (!this.composerInput()) {
        return;
      }
      untracked(() => {
        this.panel.clearPendingDraft();
        this.appendTextToComposer(draft);
      });
    });

    // Resolve the campaign name for the chip: only while open and campaign-scoped, once per id.
    effect(() => {
      if (!this.isOpen()) {
        return;
      }
      const campaignId = this.contextCampaignId();
      if (campaignId === undefined || untracked(this.resolvedCampaign)?.id === campaignId) {
        return;
      }
      this.campaignService
        .getCampaignById(campaignId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (campaign) => {
            if (this.contextCampaignId() === campaignId) {
              this.resolvedCampaign.set({ id: campaignId, name: campaign?.name ?? '' });
            }
          },
          error: () => {
            // best-effort
          },
        });
    });

    // Release the mic if the panel closes or history hides the mic button while starting or recording
    // (discards the clip), so the recording indicator doesn't linger.
    effect(() => {
      const state = this.voiceState();
      if ((!this.isOpen() || this.showHistory()) && (state === 'starting' || state === 'recording')) {
        this.cancelRecording();
      }
    });
    this.destroyRef.onDestroy(() => this.cancelRecording());
  }

  protected close(): void {
    this.panel.close();
  }

  protected onResizeStart(event: PointerEvent): void {
    if (this.isMobile() || event.button > 0) {
      return;
    }
    event.preventDefault();
    // Capture so the handle's move/up listeners keep firing off the handle and over an iframe (e.g. the editor canvas).
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    this.resizing.set(true);
    document.body.classList.add(RESIZING_CLASS);
  }

  protected onResizeMove(event: PointerEvent): void {
    if (!this.resizing()) {
      return;
    }
    // Panel hugs the right edge: width = gap from pointer to that edge.
    this.panel.setWidth(window.innerWidth - event.clientX);
  }

  protected onResizeEnd(): void {
    if (!this.resizing()) {
      return;
    }
    this.resizing.set(false);
    document.body.classList.remove(RESIZING_CLASS);
    this.panel.persistWidth();
  }

  // Left widens the panel (its edge moves left), Right narrows it; Shift takes a larger step.
  protected onResizeKeydown(event: KeyboardEvent): void {
    const step = event.shiftKey ? 40 : 10;
    let delta = 0;
    if (event.key === 'ArrowLeft') {
      delta = step;
    } else if (event.key === 'ArrowRight') {
      delta = -step;
    } else {
      return;
    }
    event.preventDefault();
    const current = this.panelEl()?.nativeElement.offsetWidth ?? 0;
    this.panel.setWidth(current + delta);
    this.panel.persistWidth();
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: KeyboardEvent): void {
    // A CDK overlay (dialog/menu/flyout) on top calls preventDefault when it handles Escape; don't
    // also close the panel behind it.
    if (event.defaultPrevented) {
      return;
    }
    if (this.isOpen()) {
      this.close();
    }
  }

  protected send(): void {
    const editor = this.composerInput()?.nativeElement;
    if (!editor || this.streaming()) {
      return;
    }
    const { text, mentions } = serializeEditor(editor);
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      return;
    }
    this.closeMentions();
    this.isPinnedToBottom.set(true);
    this.store.send(trimmed, mentions.length > 0 ? mentions : undefined);
    this.clearComposer();
  }

  protected sendExample(prompt: string): void {
    this.isPinnedToBottom.set(true);
    this.store.send(prompt);
  }

  protected cancel(): void {
    this.store.cancel();
  }

  protected newConversation(): void {
    this.showHistory.set(false);
    this.store.reset();
    this.clearComposer();
  }

  protected toggleHistory(): void {
    this.showHistory.update((open) => !open);
  }

  protected onApprove(confirmation: PendingConfirmation): void {
    this.isPinnedToBottom.set(true);
    this.store.approve(confirmation, this.translate.instant('agent.approved'));
  }

  protected onDeny(confirmation: PendingConfirmation): void {
    this.isPinnedToBottom.set(true);
    this.store.deny(confirmation, this.translate.instant('agent.declined'));
  }

  protected onPickTemplate(selection: TemplateSelection): void {
    this.isPinnedToBottom.set(true);
    this.store.pickTemplate(selection, this.translate.instant('agent.template.pick-message', { name: selection.name }));
  }

  protected onRetry(): void {
    this.isPinnedToBottom.set(true);
    this.store.retry();
  }

  protected onRate(event: { messageId: string; rating: TurnRating }): void {
    this.store.rate(event.messageId, event.rating);
  }

  // The AudienceHub flyout self-loads widgets, active apps, and the account; it only needs the
  // audience to edit. On mobile the panel covers the screen, so close it first.
  protected onOpenAudience(audience: Audience): void {
    if (this.isMobile()) {
      this.close();
    }
    this.flyoutService.open({ type: FlyoutDataType.AudienceHub, audience } as FlyoutOpeningData);
  }

  protected onKeydown(event: KeyboardEvent): void {
    // An IME owns its keys mid-composition (Enter picks a candidate); Safari reports that Enter as 229.
    if (event.isComposing || event.keyCode === 229) {
      return;
    }
    if (this.mentionOpen() && this.handleMentionKeydown(event)) {
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
      return;
    }
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      this.insertLineBreak();
      return;
    }
    // A chip is contenteditable=false, so the browser already treats it as atomic, but the exact
    // behaviour when the caret abuts one is inconsistent, so handle it ourselves: one Backspace/Delete
    // removes the whole chip. This is what makes a half-deleted "@Summer Sale" -> "@Summer" impossible.
    if (event.key === 'Backspace' && this.removeAdjacentChip('backward')) {
      event.preventDefault();
      return;
    }
    if (event.key === 'Delete' && this.removeAdjacentChip('forward')) {
      event.preventDefault();
    }
  }

  // Returns true when the key was consumed by the mention overlay (the caller then stops).
  private handleMentionKeydown(event: KeyboardEvent): boolean {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveMention(1);
        return true;
      case 'ArrowUp':
        event.preventDefault();
        this.moveMention(-1);
        return true;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        this.selectMention(this.mentionActiveIndex());
        return true;
      case 'Escape':
        event.preventDefault();
        this.closeMentions();
        return true;
      default:
        return false;
    }
  }

  protected onComposerInput(): void {
    const editor = this.composerInput()?.nativeElement;
    if (!editor) {
      return;
    }
    this.refreshComposerState();
    const token = findMentionToken(editor);
    if (!token) {
      this.closeMentions();
      return;
    }
    this.mentionToken = token;
    this.mentionQuery.set(token.query);
    void this.refreshMentionResults(token.query);
  }

  // Moving the caret fires no input event, so an open overlay closes once the caret leaves its token.
  protected onComposerCaretMove(): void {
    const token = this.mentionToken;
    if (this.mentionQuery() === null || !token) {
      return;
    }
    const selection = window.getSelection();
    const inToken =
      !!selection?.isCollapsed &&
      selection.anchorNode === token.node &&
      selection.anchorOffset > token.start &&
      selection.anchorOffset <= token.end;
    if (!inToken) {
      this.closeMentions();
    }
  }

  protected onComposerKeyup(event: KeyboardEvent): void {
    if (CARET_KEYS.has(event.key)) {
      this.onComposerCaretMove();
    }
  }

  // Overlay rows prevent blur via mousedown, so a real blur means focus left the composer entirely.
  protected onComposerBlur(): void {
    this.closeMentions();
  }

  // Drop is coerced to plain text like paste. Handled at beforeinput so an in-editor drag still
  // removes its source (deleteByDrag) and the text lands where it was dropped.
  protected onBeforeInput(event: InputEvent): void {
    if (event.inputType !== 'insertFromDrop') {
      return;
    }
    event.preventDefault();
    const text = event.dataTransfer?.getData('text/plain') ?? '';
    const editor = this.composerInput()?.nativeElement;
    const selection = window.getSelection();
    if (text.length === 0 || !editor || !selection) {
      return;
    }
    editor.focus();
    const target = event.getTargetRanges()[0];
    if (target) {
      const range = document.createRange();
      range.setStart(target.startContainer, target.startOffset);
      range.setEnd(target.endContainer, target.endOffset);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    document.execCommand('insertText', false, text);
    this.refreshComposerState();
  }

  // Paste is coerced to plain text: contenteditable pastes rich HTML by default, which would smuggle
  // arbitrary markup (and fake chips) into the editor.
  protected onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (text.length === 0) {
      return;
    }
    document.execCommand('insertText', false, text);
    this.refreshComposerState();
  }

  protected selectMention(index: number): void {
    const option = this.mentionResults()[index];
    const editor = this.composerInput()?.nativeElement;
    const token = this.mentionToken;
    if (!option || !editor || !token) {
      return;
    }
    editor.focus();
    const range = document.createRange();
    range.setStart(token.node, token.start);
    range.setEnd(token.node, token.end);
    this.selectRange(range);
    // Through execCommand so Ctrl+Z undoes the pick. The trailing space puts the caret after the chip,
    // so typing continues outside it.
    document.execCommand('insertHTML', false, `${createMentionChip(option).outerHTML} `);
    this.closeMentions();
    this.refreshComposerState();
  }

  private moveMention(delta: number): void {
    const count = this.mentionResults().length;
    if (count === 0) {
      return;
    }
    this.mentionActiveIndex.update((index) => (index + delta + count) % count);
  }

  private async refreshMentionResults(query: string): Promise<void> {
    const [sites, audiences, campaigns] = await Promise.all([
      this.siteSource.sites(),
      this.audienceSource.audiences(),
      this.campaignSource.campaigns(),
    ]);
    // A later keystroke may have moved the query on while we awaited the first-time load.
    if (this.mentionQuery() !== query) {
      return;
    }
    this.mentionResults.set(rankMentions([...sites, ...audiences, ...campaigns], query, MENTION_RESULT_LIMIT));
    this.mentionActiveIndex.set(0);
  }

  private closeMentions(): void {
    this.mentionQuery.set(null);
    this.mentionResults.set([]);
    this.mentionActiveIndex.set(0);
    this.mentionToken = null;
  }

  // A visually-empty editor can still hold a stray <br> the browser leaves after the last character
  // is deleted; collapse it to truly empty so the placeholder shows and Enter can't send whitespace.
  private refreshComposerState(): void {
    const editor = this.composerInput()?.nativeElement;
    if (!editor) {
      this.composerHasContent.set(false);
      return;
    }
    const { text, mentions } = serializeEditor(editor);
    const hasContent = text.trim().length > 0 || mentions.length > 0;
    if (!hasContent && editor.innerHTML.length > 0) {
      editor.innerHTML = '';
    }
    this.composerHasContent.set(hasContent);
  }

  private clearComposer(): void {
    const editor = this.composerInput()?.nativeElement;
    if (editor) {
      editor.innerHTML = '';
    }
    this.composerHasContent.set(false);
    this.closeMentions();
  }

  // Returns true when it removed a chip, so the caller suppresses the key's default.
  private removeAdjacentChip(direction: 'backward' | 'forward'): boolean {
    const editor = this.composerInput()?.nativeElement;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0 || !selection.isCollapsed) {
      return false;
    }
    const chip = adjacentMentionChip(editor, selection.getRangeAt(0), direction);
    if (!chip) {
      return false;
    }
    const range = document.createRange();
    range.selectNode(chip);
    this.selectRange(range);
    // Through execCommand so Ctrl+Z brings the chip back.
    document.execCommand('delete');
    this.refreshComposerState();
    return true;
  }

  private selectRange(range: Range): void {
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  private insertLineBreak(): void {
    document.execCommand('insertLineBreak');
    this.refreshComposerState();
  }

  private setCaretAfter(node: Node): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  protected onMessagesScroll(): void {
    const element = this.scrollContainer()?.nativeElement;
    if (element) {
      this.isPinnedToBottom.set(isNearBottom(element));
    }
  }

  protected jumpToLatest(): void {
    this.isPinnedToBottom.set(true);
    const element = this.scrollContainer()?.nativeElement;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }

  protected async toggleRecording(): Promise<void> {
    const state = this.voiceState();
    if (state === 'starting' || state === 'transcribing') {
      return;
    }
    if (state === 'recording') {
      this.stopRecording();
      return;
    }
    await this.startRecording();
  }

  private async startRecording(): Promise<void> {
    this.voiceError.set('');
    // Flip to 'starting' before the await so the click gives immediate feedback: acquiring the mic
    // (getUserMedia, plus the first-time permission prompt) is the latency the user was seeing.
    this.voiceState.set('starting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.voiceState.set('idle');
      this.voiceError.set(this.translate.instant('agent.voice.permission-denied'));
      return;
    }
    // Cancelled while we were acquiring (e.g. panel closed), so release the just-opened mic and bail.
    if (this.voiceState() !== 'starting') {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.mediaStream = stream;
    this.recordedChunks = [];
    try {
      const recorder = new MediaRecorder(stream);
      this.mediaRecorder = recorder;
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      });
      recorder.addEventListener('stop', () => void this.onRecordingStopped());
      recorder.start();
      this.voiceState.set('recording');
    } catch {
      // MediaRecorder unsupported for the stream/codec: release the mic and surface the failure.
      this.stopStream();
      this.mediaRecorder = undefined;
      this.voiceState.set('idle');
      this.voiceError.set(this.translate.instant('agent.voice.failed'));
    }
  }

  // The recorder's stop event drives the upload (onRecordingStopped).
  private stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.voiceState.set('transcribing');
      this.mediaRecorder.stop();
    } else {
      this.voiceState.set('idle');
    }
  }

  // Abort without transcribing (panel closed / component destroyed mid-recording). Leaves the state
  // at 'idle'; the recorder's stop handler keys off that to skip the upload (see onRecordingStopped).
  private cancelRecording(): void {
    const recorder = this.mediaRecorder;
    this.mediaRecorder = undefined;
    this.recordedChunks = [];
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        // Already stopped; nothing to do.
      }
    }
    this.stopStream();
    this.voiceState.set('idle');
  }

  private async onRecordingStopped(): Promise<void> {
    // stop() also fires on cancel, and its trailing dataavailable can refill the chunk buffer even
    // after cancelRecording cleared it, so key off state, not the buffer: only the deliberate stop
    // path sets 'transcribing'. A cancel leaves 'idle' and has already cleaned up, so bail here.
    if (this.voiceState() !== 'transcribing') {
      return;
    }
    this.stopStream();
    const chunks = this.recordedChunks;
    this.recordedChunks = [];
    const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
    this.mediaRecorder = undefined;
    if (chunks.length === 0) {
      this.voiceState.set('idle');
      return;
    }
    const blob = new Blob(chunks, { type: mimeType });
    try {
      const text = (await this.chat.transcribe(blob, fileNameForMime(mimeType))).trim();
      if (text.length > 0) {
        this.appendTextToComposer(text);
      } else {
        this.focusComposer();
      }
    } catch {
      this.voiceError.set(this.translate.instant('agent.voice.failed'));
    } finally {
      this.voiceState.set('idle');
    }
  }

  private stopStream(): void {
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = undefined;
  }

  private focusComposer(): void {
    const input = this.composerInput()?.nativeElement;
    if (input) {
      requestAnimationFrame(() => input.focus());
    }
  }

  private appendTextToComposer(text: string): void {
    const editor = this.composerInput()?.nativeElement;
    if (!editor) {
      return;
    }
    const existing = serializeEditor(editor).text;
    const prefix = existing.length > 0 && !/\s$/.test(existing) ? ' ' : '';
    editor.appendChild(document.createTextNode(prefix + text));
    this.refreshComposerState();
    const last = editor.lastChild;
    if (last) {
      this.setCaretAfter(last);
    }
    requestAnimationFrame(() => editor.focus());
  }
}

const RESIZING_CLASS = 'agent-panel-resizing';

type VoiceState = 'idle' | 'starting' | 'recording' | 'transcribing';

const MENTION_RESULT_LIMIT = 8;

const CARET_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

// Whisper infers the format from the extension; match the MediaRecorder output (webm in Chromium,
// mp4 in Safari).
function fileNameForMime(mimeType: string): string {
  if (mimeType.includes('mp4')) {
    return 'audio.mp4';
  }
  if (mimeType.includes('ogg')) {
    return 'audio.ogg';
  }
  if (mimeType.includes('wav')) {
    return 'audio.wav';
  }
  if (mimeType.includes('mpeg')) {
    return 'audio.mp3';
  }
  return 'audio.webm';
}

// px from the bottom within which the user counts as "following" (auto-scroll stays on).
const BOTTOM_THRESHOLD_PX = 60;

export function isNearBottom(el: { scrollHeight: number; scrollTop: number; clientHeight: number }): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD_PX;
}
